<template>
  <div>
    
    <div class="tr header" v-if="index === 0">
      <div class="th lrr-id">lrrId / lrrUuid</div>
      <div class="th tags">Tags</div>
      <div class="th cnx-state">cnx state</div>
      <div class="th health-traffic">Health &amp; Traffic</div>
      <div class="th connections">connections</div>
      <div class="th location">Location</div>
    </div>

    <div class="tr" :class="{even: index % 2 === 0}">
      <div class="td lrr-id">
        <div class="fw-bold" v-if="baseStation.lrrId !== undefined && baseStation.lrrUuid !== undefined">{{baseStation.lrrId }} / {{baseStation.lrrUuid}}</div>
        <div class="fw-bold" v-else>{{baseStation.lrrRef }}</div>
        <div :title="baseStation.name">{{formatName(baseStation.name)}}</div>
        <div>
          <span class="text-dark small"><font-awesome-icon icon="mobile-alt"/> {{baseStation.commercialName}} / {{baseStation.version}}</span>
        </div>
        <div><span class="text-dark small">{{baseStation.supplierId}}</span></div>
      </div>
      <div class="td tags">
        <div v-for="tag in baseStation.tags" :key="tag" class="ow">
          <span class="badge bg-success rounded-pill" >{{tag}}</span>
        </div>
      </div>
      <div class="td cnx-state">
        <span class="badge bg-success" v-if="baseStation.cnxState === 'CNX'">{{baseStation.cnxState}}</span>
        <span class="badge bg-danger" v-if="baseStation.cnxState === 'DISC'">{{baseStation.cnxState}}</span>
        <div>{{formatTs(baseStation.cnxStateSince).time}}</div>
        <div>{{formatTs(baseStation.cnxStateSince).ago}}</div>
      </div>
      <div class="td health-traffic">
        <span class="badge bg-danger" v-if="baseStation.healthState.includes('ERROR')">{{baseStation.healthState}}</span>
        <span class="badge bg-success" v-if="!baseStation.healthState.includes('ERROR')">{{baseStation.healthState}}</span>
        <div><font-awesome-icon icon="upload"/> {{formatTs(baseStation.lastUpFrame).time}}</div>
        <div><font-awesome-icon icon="download"/> {{formatTs(baseStation.lastDwFrame).time}}</div>
      </div>

      <div class="td connections">
        <div v-for="conn in baseStation.connList" :key="conn.name">
          <span class="text-dark small">{{conn.name}} | {{conn.type}} | {{conn.state}} | <font-awesome-icon icon="upload"/> avg {{conn.txAvgRate}} | <font-awesome-icon icon="download"/> avg {{conn.rxAvgRate}}</span>
        </div>        
      </div>

      <div class="td location">
        <div v-if="baseStation.location">
          <div><span class="text-dark small">LAT {{baseStation.location.lat}}</span></div>
          <div><span class="text-dark small">LONG {{baseStation.location.long}}</span></div>
          <div>
          <a
:href="`https://www.google.com/maps/search/?api=1&query=${baseStation.location.lat},${baseStation.location.long}`"
            target="_blank"
            class="cp" 
            data-tippy-content="open in Google Maps"
>

            <font-awesome-icon icon="external-link-alt"/>
          </a>
          </div>
        </div>

        <div v-if="!baseStation.location">
          <span class="text-dark small">N/A</span>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import dayjs from '@/plugins/dayjs'
import tippy from 'tippy.js'
import type { BaseStation } from '@/types'

interface Props {
  baseStation: BaseStation
  index: number
}

withDefaults(defineProps<Props>(), {
  baseStation: () => ({} as BaseStation),
  index: 0
})

onMounted(() => {
  tippy('[data-tippy-content]')
})

const formatTs = (ts: number | undefined): { time: string, ago: string } => {
  return {
    time: ts ? dayjs(ts).format('L LT') : 'N/A',
    ago: ts ? dayjs(ts).fromNow() : 'N/A'
  }
}

const formatName = (name: string): string => {
  if (name.length <= 30)
    return name
  return name.substring(0, 27) + '...'
}
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

.tr {
  display: flex;
  flex-direction: row;
  text-align: left;
  justify-content: space-between;
  margin-bottom: 2px;
}

.tr.even {
  background-color: $bgColourLight;
}

.tr.header {
  background-color: white;
  border-bottom: 1px solid $bgColour;
}

.th {
  color: $bgColour;
  font-weight: bold;
}

.th, .td {
  padding: 4px;
}

.td.lrr-id, .th.lrr-id {
  width: 25%;
}

.td.tags, .th.tags {
  width: 14%;
}

.td.cnx-state, .th.cnx-state {
  width: 14%;
}

.td.health-traffic, .th.health-traffic {
  width: 16%;
}

.td.connections, .th.connections {
  width: 25%;
}

.td.location, .th.location {
  width: 10%;
}

</style>